import { generateHash } from '../../../utils/caching/common/key-generator';
import { type CacheAnalyticsCallback, promiseCache } from '../../../utils/caching/persist-refresh';
import { DegradablePromise, type DegradationCallback } from '../../utils/degradable-promise';
import {
	ONE_DAY_MS_TTL,
	ONE_HOUR_MS_TTL,
	SEARCH_PERSONALIZATION_BY_SITE_USER_CACHE_KEY,
	SEVEN_DAY_MS_TTL,
} from './constants';
import { getPersonalizationSiteUserTraitsUrl } from './get-personalization-site-user-traits-url';
import { type Trait, type Traits } from './types';

// eslint-disable-next-line no-barrel-files/no-barrel-files
export { getPersonalizationSiteUserTraitsUrl } from './get-personalization-site-user-traits-url';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { parseRecommendations } from './parse-recommendations';

const {
	refreshOnAccessDecorator: refreshOnAccessDecoratorPersonalization,
	get: getPersonalizationCache,
	clear: clearPersonalizationCache,
} = promiseCache<Trait | undefined>({
	name: SEARCH_PERSONALIZATION_BY_SITE_USER_CACHE_KEY,
	expiration: SEVEN_DAY_MS_TTL,
});

export const fetchUserPersonalizationService: (userId: string) => Promise<Traits> = async (
	userId: string,
): Promise<Traits> => {
	const requestUrl = getPersonalizationSiteUserTraitsUrl(userId);
	const requestOptions: RequestInit = {
		method: 'GET',
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
		},
	};

	return fetch(requestUrl, requestOptions).then((response: Response) => {
		if (response.ok) {
			return response.json();
		}

		return Promise.reject({
			code: response.status,
			reason: response.statusText,
		});
	});
};

export const fetchUserPersonalizationBySiteUser: ({
	userId,
	analyticsCallback,
	cacheKeySuffix,
	traitName,
	degradationCallback,
}: {
	userId: string;
	analyticsCallback?: CacheAnalyticsCallback;
	cacheKeySuffix?: string;
	traitName: string;
	degradationCallback?: DegradationCallback;
}) => Promise<Trait | undefined> = async ({
	userId,
	analyticsCallback,
	cacheKeySuffix,
	traitName,
	degradationCallback = () => {},
}: {
	userId: string;
	analyticsCallback?: CacheAnalyticsCallback;
	cacheKeySuffix?: string;
	traitName: string;
	degradationCallback?: DegradationCallback;
}) => {
	const key = generateHash(
		`${SEARCH_PERSONALIZATION_BY_SITE_USER_CACHE_KEY}|${userId}|${cacheKeySuffix}`,
	);

	const supplier = async () => {
		const fetchPromise = fetchUserPersonalizationService(userId)
			.then((data: Traits) => {
				const { attributes } = data;
				return attributes?.find((t) => t.name === traitName);
			})
			.catch(() => undefined); // treat non-200 as empty and avoid caching a rejected promise

		const degrader = new DegradablePromise(fetchPromise, degradationCallback, 1000); // 1s degrade threshold

		return degrader.promise().catch(async (e) => {
			// On degrade/timeout, re-set cache entry to resolved undefined with ONE_HOUR_TTL
			// so callers won't hammer the service for the next hour, but will retry after.
			// Since we're in supplier, we can't directly set here without re-entrant complexity.
			// Instead, throw and let refreshOnAccessDecorator store this rejecting promise; after this call,
			// callers should rely on our external code path to clear or retry as needed.
			throw e;
		});
	};

	const existing = getPersonalizationCache(key.toString());
	// Choose TTL based on outcome:
	// - success (non-empty trait): default SEVEN_DAY_TTL
	// - empty (undefined): ONE_DAY_TTL
	// - degraded (timeout/reject): ONE_HOUR_TTL
	// We cannot know the outcome until supplier resolves/rejects; so we approximate:
	//   if no existing value, we assume empty path (ONE_HOUR_MS_TTL) to avoid hammering
	//   then, within supplier, we adjust by re-setting the cache on rejection with ONE_HOUR_TTL
	const effectiveExpiration = !existing?.storedValue ? ONE_HOUR_MS_TTL : undefined;
	const effectiveOnlyRefreshIfExpired = true;

	// Helper to re-write the cached value with a specific TTL without re-fetching
	const writeBackWithTTL = (value: Trait | undefined, ttl: number) => {
		refreshOnAccessDecoratorPersonalization({
			key: key.toString(),
			supplier: async () => value,
			returnExpired: false,
			onlyRefreshIfExpired: false, // force refresh to apply new TTL
			expiration: ttl,
		});
	};

	return refreshOnAccessDecoratorPersonalization({
		key: key.toString(),
		supplier: async () => {
			try {
				const result = await supplier(); // Trait | undefined (no degrade)
				if (result) {
					writeBackWithTTL(result, SEVEN_DAY_MS_TTL);
				} else {
					writeBackWithTTL(undefined, ONE_DAY_MS_TTL);
				}
				return result;
			} catch (e) {
				// On degrade/timeout, return undefined so promiseCache stores an "empty" value
				// and then write back a short TTL to avoid hammering
				writeBackWithTTL(undefined, ONE_HOUR_MS_TTL);
				return undefined;
			}
		},
		onlyRefreshIfExpired: effectiveOnlyRefreshIfExpired,
		expiration: effectiveExpiration,
		analyticsCallback,
	});
};

// Mainly for testing, clearing cache
export const __test_clearPersonalizationCache: (key?: string) => void = (key?: string): void =>
	clearPersonalizationCache(key);
